import React, { useState, useEffect, useCallback } from 'react';
import { AlertCircle, Upload, CalendarCheck, Loader2 } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import apiClient from '../../utils/apiClient';
import { toast } from 'sonner';

/**
 * BankGapBanner — shown under a connected (Mono/Plaid) bank tile when the feed's earliest transaction is later than
 * the accounting start date. Start-date rules v2: the only accounting-sound resolutions are
 *   1. upload a statement covering the missing window (partial fills shrink the window to what is still missing), or
 *   2. declare that the account did not exist at the start date (opening balance 0).
 * "Skip" and "start this bank later" were removed — they silently dropped real transactions.
 */
export default function BankGapBanner({ bankAccountId, onUploadClick, onResolved }) {
  const [state, setState] = useState({ loading: true });
  const [submitting, setSubmitting] = useState(false);
  const [showOpened, setShowOpened] = useState(false);
  const [openedOn, setOpenedOn] = useState('');

  const load = useCallback(async () => {
    try {
      const { data } = await apiClient.get(`/banking/${bankAccountId}/backfill-gap`);
      setState({ loading: false, ...data });
    } catch (_) {
      setState({ loading: false, has_gap: false });
    }
  }, [bankAccountId]);

  useEffect(() => { load(); }, [load]);

  if (state.loading) return null;
  if (!state.has_gap) return null;
  if (state.status && state.status !== 'unresolved') return null;

  const declareOpenedLater = async () => {
    if (!openedOn) return;
    setSubmitting(true);
    try {
      const { data } = await apiClient.post('/bank-feeds/gap-fill/account-opened-later', { bank_account_id: bankAccountId, opened_on: openedOn });
      if (data?.ok) {
        toast.success('Recorded: this account opened after your start date — opening balance set to 0.');
        setState((s) => ({ ...s, status: 'account_opened_later' }));
        onResolved?.();
      } else {
        toast.error(data?.reason || 'Could not record that.');
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not save your choice. Please try again.');
    } finally { setSubmitting(false); }
  };

  const residual = state.residual;
  const join = state.join_check;

  return (
    <div className="mt-2 border border-sky-300 bg-sky-50/60 rounded-md p-3 text-sm" data-testid={`gap-banner-${bankAccountId}`}>
      <div className="flex items-start gap-2">
        <AlertCircle className="w-4 h-4 text-sky-700 flex-shrink-0 mt-0.5" />
        <div className="flex-1">
          <div className="font-semibold text-sky-900">
            {residual ? 'Statement still needed for part of the gap' : `Backlog gap detected for ${state.bank_name}`}
          </div>
          <div className="text-xs text-sky-800 mt-0.5" data-testid={`gap-banner-window-${bankAccountId}`}>
            {residual ? (
              <>Your statement covered up to <strong>{join?.stmt_end}</strong>, but the books open on <strong>{join?.next_row_date}</strong> at a
                different balance ({Number(join?.next_row_open).toLocaleString()} vs {Number(join?.statement_close).toLocaleString()}),
                so transactions are still missing between <strong>{residual.start}</strong> and <strong>{residual.end}</strong>.</>
            ) : (
              <>The bank feed could only fetch back to <strong>{state.bank_first_transaction_date}</strong>; your accounting start date is{' '}
                <strong>{state.accounting_start_date}</strong>. Upload a statement covering <strong>{state.gap_start}</strong> → <strong>{state.gap_end}</strong>{' '}
                ({state.gap_days} days) so the opening balance and every transaction from your start date are in the books.</>
            )}
          </div>

          <div className="mt-3 flex flex-wrap gap-2">
            <Button size="sm" disabled={submitting} className="bg-sky-700 hover:bg-sky-800 text-white"
              onClick={() => { onUploadClick?.(bankAccountId); }} data-testid={`gap-banner-upload-${bankAccountId}`}>
              <Upload className="w-3.5 h-3.5 mr-1" />
              Upload statement to fill gap
            </Button>
            {!residual && (
              <Button size="sm" variant="outline" disabled={submitting} className="border-sky-400 text-sky-900 hover:bg-sky-100"
                onClick={() => setShowOpened((v) => !v)} data-testid={`gap-banner-opened-later-${bankAccountId}`}>
                <CalendarCheck className="w-3.5 h-3.5 mr-1" />
                Account opened after start date
              </Button>
            )}
          </div>

          {showOpened && !residual && (
            <div className="mt-3 flex flex-wrap items-center gap-2" data-testid={`gap-banner-opened-inline-${bankAccountId}`}>
              <Input type="date" value={openedOn} min={state.accounting_start_date || undefined} max={state.bank_first_transaction_date || undefined}
                onChange={(e) => setOpenedOn(e.target.value)} className="w-44 h-8" data-testid={`gap-banner-opened-input-${bankAccountId}`} />
              <Button size="sm" disabled={submitting || !openedOn} onClick={declareOpenedLater}
                className="bg-sky-700 hover:bg-sky-800 text-white" data-testid={`gap-banner-opened-save-${bankAccountId}`}>
                {submitting ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : 'Account opened on this date'}
              </Button>
              <span className="text-[11px] text-sky-800">Only if the account truly did not exist on {state.accounting_start_date} — its opening balance becomes 0.</span>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
